import React, { useEffect, useRef, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Bus, MapPin, Clock, Zap, Users, Route } from "lucide-react";

const BASE_POINTS = [
  { x: 60, y: 160, icon: MapPin, label: "Start", color: "bg-green-500" },
  { x: 160, y: 65, icon: Users, label: "Stop 1", color: "bg-blue-500" },
  { x: 290, y: 160, icon: Clock, label: "Stop 2", color: "bg-purple-500" },
  { x: 420, y: 160, icon: MapPin, label: "End", color: "bg-red-500" },
];

const BUS_BASE_X = [60, 160, 290, 420];
const BUS_BASE_Y = [160, 65, 160, 160];

const AnimatedRoutes: React.FC = React.memo(() => {
  // Base canvas used to design the coordinates
  const BASE_W = 540;
  const BASE_H = 360;

  // Measure container to scale positions
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: BASE_W, height: BASE_H });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      setSize({ width: el.clientWidth, height: el.clientHeight });
    };
    measure();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => measure());
      ro.observe(el);
      return () => ro.disconnect();
    } else {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
  }, []);

  const sx = size.width / BASE_W;
  const sy = size.height / BASE_H;

  // Memoize scaled positions
  const busX = useMemo(() => BUS_BASE_X.map((v) => v * sx), [sx]);
  const busY = useMemo(() => BUS_BASE_Y.map((v) => v * sy), [sy]);
  const points = BASE_POINTS;

  return (
    <div className="relative w-full flex items-center justify-center overflow-hidden">
      <div
        ref={containerRef}
        className="relative w-full max-w-[540px] aspect-[3/2]"
      >
        {/* Grid Background */}
        <div className="absolute inset-0">
          <svg width="100%" height="100%" className="text-amber-400/20">
            <defs>
              <pattern
                id="gridPattern"
                width="30"
                height="30"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 30 0 L 0 0 0 30"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                />
              </pattern>
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <rect width="100%" height="100%" fill="url(#gridPattern)" />
          </svg>

          <motion.div
            className="absolute inset-0 bg-gradient-radial from-amber-400/10 via-transparent to-transparent"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>

        {/* Bus Animation */}
        <motion.div
          className="absolute z-20"
          animate={{
            x: busX,
            y: busY,
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <motion.div
            className="relative"
            animate={{
              rotateY: [0, 5, 0, -5, 0],
              rotateX: [0, 2, 0, -2, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <div className="absolute top-8 left-1 w-12 h-6 bg-black/20 rounded-full blur-sm transform scale-x-150" />
            <div className="relative bg-gradient-to-br from-amber-300 to-amber-500 p-4 rounded-xl border-2 border-amber-200 shadow-2xl transform perspective-1000">
              <div className="absolute top-1 left-1 w-3 h-3 bg-white/60 rounded-full blur-sm" />
              <Bus size={24} className="text-gray-900 relative z-10" />
              <motion.div
                className="absolute -top-2 -right-2 w-4 h-4 bg-primary-500 rounded-full border-2 border-white"
                animate={{
                  scale: [1, 1.3, 1],
                  boxShadow: [
                    "0 0 0 0 rgba(34, 197, 94, 0.7)",
                    "0 0 0 8px rgba(34, 197, 94, 0)",
                    "0 0 0 0 rgba(34, 197, 94, 0)",
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
              />
            </div>
          </motion.div>
        </motion.div>

        {/* Route Points */}
        {points.map((point, index) => (
          <motion.div
            key={index}
            className="absolute z-10"
            style={{
              left: point.x * sx - (size.width < 380 ? 12 : 16),
              top: point.y * sy - (size.width < 380 ? 12 : 16),
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: [0, 1, 1.1, 1],
              opacity: [0, 1, 1, 1],
            }}
            transition={{
              duration: 0.6,
              delay: index * 0.3,
            }}
          >
            <motion.div
              className={`absolute inset-0 ${point.color} rounded-full blur-md`}
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.6, 0.3, 0.6],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: index * 0.5,
              }}
            />
            <div
              className={`relative ${size.width < 380 ? "w-6 h-6" : "w-8 h-8"} ${point.color} rounded-full border-2 border-white shadow-md flex items-center justify-center`}
            >
              <point.icon
                className={`${size.width < 380 ? "w-3.5 h-3.5" : "w-4 h-4"}`}
              />
            </div>
            <motion.div
              className={`absolute ${size.width < 380 ? "-bottom-6" : "-bottom-8"} left-1/2 transform -translate-x-1/2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded ${size.width < 380 ? "text-[10px]" : "text-xs"} font-medium text-gray-800 whitespace-nowrap`}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.3 + 0.5 }}
            >
              {point.label}
            </motion.div>
          </motion.div>
        ))}

        {/* Route Lines */}
        <svg
          className="absolute inset-0 w-full h-full z-5"
          viewBox="0 0 540 360"
          preserveAspectRatio="none"
        >
          <motion.path
            d="M 60 160 Q 110 112 160 65 Q 225 112 290 160 Q 355 160 420 160"
            stroke="#fbbf24"
            strokeWidth="3"
            strokeDasharray="8,4"
            fill="none"
            filter="url(#glow)"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          <motion.path
            d="M 60 160 Q 125 125 160 65 Q 215 100 290 160 Q 375 165 420 160"
            stroke="#10b981"
            strokeWidth="2"
            strokeDasharray="4,8"
            strokeOpacity="0.6"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 5, repeat: Infinity, delay: 1 }}
          />
        </svg>

        {/* Data Cards */}
        <motion.div
          className="absolute top-4 right-4 bg-white/95 backdrop-blur-md rounded-lg p-3 border border-amber-200 shadow-xl"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 2 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            <div className="text-xs font-semibold text-gray-800">Bus #2847</div>
          </div>
          <div className="text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <Clock size={10} />
              <span>On Time</span>
            </div>
            <div className="flex items-center gap-1 mt-1">
              <Zap size={10} />
              <span>3 min away</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-md rounded-lg p-3 border border-amber-200 shadow-xl"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 2.5 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Route size={12} className="text-amber-600" />
            <div className="text-xs font-semibold text-gray-800">
              Route Optimized
            </div>
          </div>
          <div className="text-xs text-gray-600">
            <div>Traffic: Light</div>
            <div className="text-green-600 font-medium">15% faster</div>
          </div>
        </motion.div>

        {/* Particles */}
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-amber-400/60 rounded-full"
            style={{
              left: `${15 + i * 13}%`,
              top: `${20 + (i % 3) * 20}%`,
            }}
            animate={{
              y: [-10, 10, -10],
              opacity: [0.3, 1, 0.3],
              scale: [0.8, 1.2, 0.8],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: i * 0.5,
              ease: "easeInOut",
            }}
          />
        ))}

        {/* Speed Lines */}
        <motion.div
          className="absolute inset-0 overflow-hidden"
          animate={{
            x: [-600, 600],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear",
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="absolute h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent"
              style={{
                top: `${40 + i * 15}%`,
                width: "200px",
                left: "-200px",
              }}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
});

AnimatedRoutes.displayName = 'AnimatedRoutes';
export default AnimatedRoutes;
